import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Input,
  Button,
  FormControl,
  InputText,
  Flex,
  InputSelect,
  Modalv2,
  ModalHeader,
  ModalBody,
  ModalFooter
} from '@itandi/itandi-bb-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-toastify';
import type {
  CompanyStoreSetting,
  CompanyStoreSettingEdit
} from '~/models/CompanyStoreSetting';
import { validationSchemaCompanyStoreSetting } from '~/models/CompanyStoreSetting';
import Styles from './CompanyStoreSettingForm.module.scss';
import { update } from '~/utils/api/CompanyStoreSetting';
import { isSuccess } from '~/utils/api/api_handler';
import type { Prefecture } from '~/constants/PrefectureConst';
import { prefectures } from '~/constants/PrefectureConst';

type Props = {
  companyStoreSetting: CompanyStoreSetting;
};

export const CompanyStoreSettingForm: React.FC<Props> = ({
  companyStoreSetting
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const { register, getValues, formState, handleSubmit } =
    useForm<CompanyStoreSettingEdit>({
      defaultValues: companyStoreSetting,
      mode: 'onBlur',
      resolver: yupResolver(validationSchemaCompanyStoreSetting)
    });
  const onClickSubmit = React.useCallback(() => {
    handleSubmit(() => {
      setIsConfirmModalOpen(true);
    })();
  }, [handleSubmit]);
  const handleAccept = React.useCallback(async () => {
    const values = getValues();
    const response = await update(values);
    if (isSuccess(response)) {
      toast.success('保存しました');
      setIsConfirmModalOpen(false);
      return;
    }
    toast.error('保存に失敗しました');
  }, [getValues]);
  const { errors } = formState;

  return (
    <Flex direction="column" gap={16} maxWidth={600} rowGap={16}>
      <div className={Styles.FormContainer}>
        <FormControl chip="必須" label="店舗名" variant="Vertical">
          <Input isInvalid={errors.name != null} {...register('name')} />
          {errors.name != null && (
            <div className={Styles.InvalidFeedback}>{errors.name.message}</div>
          )}
        </FormControl>
        <FormControl chip="必須" label="郵便番号" variant="Vertical">
          <Input isInvalid={errors.zipcode != null} {...register('zipcode')} />
          {errors.zipcode != null && (
            <div className={Styles.InvalidFeedback}>
              {errors.zipcode.message}
            </div>
          )}
        </FormControl>
        <FormControl chip="必須" label="都道府県" variant="Vertical">
          <InputSelect w="100%" {...register('state')}>
            <option value="">選択してください</option>
            {prefectures.map((p: Prefecture) => (
              <option key={p.id} value={p.name}>
                {p.name}
              </option>
            ))}
          </InputSelect>
        </FormControl>
        <FormControl chip="必須" label="市区町村・番地" variant="Vertical">
          <Input isInvalid={errors.city != null} {...register('city')} />
          {errors.city != null && (
            <div className={Styles.InvalidFeedback}>{errors.city.message}</div>
          )}
        </FormControl>
        <FormControl label="建物・部屋番号" variant="Vertical">
          <Input {...register('otherAddress')} />
        </FormControl>
        <FormControl chip="必須" label="店舗の代表電話番号" variant="Vertical">
          <Input
            isInvalid={errors.phoneNumber != null}
            {...register('phoneNumber')}
          />
          {errors.phoneNumber != null && (
            <div className={Styles.InvalidFeedback}>
              {errors.phoneNumber.message}
            </div>
          )}
        </FormControl>
        <FormControl label="オーナー請求書用備考" variant="Vertical">
          <InputText
            maxRows={10}
            placeholder="オーナー用請求書エクセルファイルで記載したい備考を入力してください"
            resize="autoRows"
            rows={6}
            {...register('billingNoteForOwner')}
          />
        </FormControl>
        <FormControl label="入居者請求書用備考" variant="Vertical">
          <InputText
            maxRows={10}
            placeholder="入居者用請求書エクセルファイルで記載したい備考を入力してください"
            resize="autoRows"
            rows={6}
            {...register('billingNoteForRentee')}
          />
        </FormControl>
        <FormControl label="オーナー精算書用備考" variant="Vertical">
          <InputText
            maxRows={10}
            placeholder="オーナー用精算書エクセルファイルで記載したい備考を入力してください"
            resize="autoRows"
            rows={6}
            {...register('settlementNoteForOwner')}
          />
        </FormControl>
        <FormControl label="入居者精算書用備考" variant="Vertical">
          <InputText
            maxRows={10}
            placeholder="入居者用精算書エクセルファイルで記載したい備考を入力してください"
            resize="autoRows"
            rows={6}
            {...register('settlementNoteForRentee')}
          />
        </FormControl>
        <div>
          <Button onClick={onClickSubmit} variant="primary">
            保存
          </Button>
        </div>
        <Modalv2
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          size="s"
        >
          <ModalHeader title="設定の保存" />
          <ModalBody>設定を保存します。</ModalBody>
          <ModalFooter
            leftArea={
              <Button
                onClick={() => {
                  setIsConfirmModalOpen(false);
                }}
                variant="secondary"
              >
                キャンセル
              </Button>
            }
            rightArea={
              <Button onClick={handleAccept} variant="primary">
                保存
              </Button>
            }
          />
        </Modalv2>
      </div>
    </Flex>
  );
};
